import { useMemo } from 'react'
import modulosData from '@content/modulos.json'
import { GUIA_LABELS } from '@utils/guiaLabels.js'
import { CALC_GRUPOS_FLAT } from '@content/calc-search-index.js'
import { WIKI_ENTRIES } from '@content/wiki-entries.js'
import { wikiArticlePath } from '@utils/constants.js'

export type SearchResultKind = 'etapa' | 'ferramenta' | 'tela' | 'recurso' | 'wiki'

export const SEARCH_KIND_LABEL: Record<SearchResultKind, string> = {
  etapa: GUIA_LABELS.searchKindEtapa,
  ferramenta: GUIA_LABELS.searchKindFerramenta,
  tela: 'Tela',
  recurso: 'Recurso',
  wiki: 'Wiki',
}

export interface GlobalSearchHit {
  id: string
  kind: SearchResultKind
  title: string
  meta: string
  path: string
}

const ETAPA_INDEX: GlobalSearchHit[] = modulosData.modulos.flatMap((m) =>
  m.topicos.map((t) => ({
    id: `etapa-${t.id}`,
    kind: 'etapa' as const,
    title: t.titulo,
    meta: m.titulo,
    path: `/modulos/${m.id}/topicos/${t.id}`,
  })),
)

const TELAS: GlobalSearchHit[] = [
  { id: 'tela-fin', kind: 'tela', title: 'Financeiro', meta: 'Obra', path: '/financeiro' },
  { id: 'tela-doc', kind: 'tela', title: 'Arquivos da obra', meta: 'Obra', path: '/documentos' },
  { id: 'tela-dia', kind: 'tela', title: 'Diário de bordo', meta: 'Obra', path: '/diario' },
  { id: 'tela-al', kind: 'tela', title: 'Alertas', meta: 'Obra', path: '/alertas' },
  {
    id: 'tela-calc',
    kind: 'tela',
    title: GUIA_LABELS.ferramentasIndex,
    meta: 'Guia',
    path: '/calculadoras',
  },
  { id: 'tela-jor', kind: 'tela', title: 'Jornada', meta: 'Guia', path: '/modulos' },
  { id: 'tela-etapa', kind: 'tela', title: GUIA_LABELS.etapaAtual, meta: 'Guia', path: '/etapa' },
  { id: 'tela-cfg', kind: 'tela', title: 'Configurações', meta: 'App', path: '/config' },
  { id: 'tela-wiki', kind: 'tela', title: 'Enciclopédia', meta: 'Guia', path: '/wiki' },
  {
    id: 'tela-mat',
    kind: 'tela',
    title: 'Orçamentos de materiais',
    meta: 'Obra',
    path: '/materiais',
  },
]

const WIKI_INDEX: GlobalSearchHit[] = WIKI_ENTRIES.map((e) => ({
  id: `wiki-${e.slug}`,
  kind: 'wiki' as const,
  title: e.titulo,
  meta: e.categoria,
  path: wikiArticlePath(e.slug),
}))

const INDEX: GlobalSearchHit[] = [...ETAPA_INDEX, ...CALC_GRUPOS_FLAT, ...WIKI_INDEX, ...TELAS]

export function searchGlobal(query: string, limit = 12): GlobalSearchHit[] {
  const q = query.trim().toLowerCase()
  if (q.length < 2) return []
  return INDEX.filter(
    (h) =>
      h.title.toLowerCase().includes(q) ||
      h.meta.toLowerCase().includes(q) ||
      (h.kind === 'ferramenta' && h.id.includes(q)) ||
      (h.kind === 'recurso' && h.id.includes(q.replace(/\s+/g, '-'))),
  ).slice(0, limit)
}

export function useGlobalSearchIndex() {
  return useMemo(() => INDEX, [])
}
